import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { aboutContent } from '@/mocks/about';
import { siteConfig } from '@/mocks/site';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qa7ffnahb7382ov8k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const formSubmitAddr = 'https://readdy.ai/api/form/d9qa7ffnahb7382ov8k0';

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Organization"
        data={{
          name: siteConfig.siteName,
          url: siteConfig.siteUrl,
          description: siteConfig.siteDescription,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `关于我们 - ${siteConfig.siteName}`,
          description: `了解${siteConfig.siteName}的品牌故事、用户口碑和联系方式`,
          url: `${siteConfig.siteUrl}/about`,
        }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Brand Story */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6 text-center">
              {aboutContent.brand.title}
            </h1>
            <div className="max-w-2xl mx-auto">
              {aboutContent.brand.story.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {aboutContent.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                {aboutContent.reputation.title}
              </h2>
              <p className="text-base text-foreground-600">{aboutContent.reputation.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {aboutContent.reputation.reviews.map((review) => (
                <div key={review.name} className="p-6 md:p-7 rounded-xl bg-background-50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-accent-600">{review.avatar}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-900 truncate">{review.name}</div>
                      <div className="text-xs text-foreground-500 truncate">{review.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                {aboutContent.contact.title}
              </h2>
              <p className="text-base text-foreground-600">{aboutContent.contact.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 max-w-5xl mx-auto">
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-lg text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">{aboutContent.contact.hours}</p>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                action={formSubmitAddr}
                method="POST"
                className="p-6 md:p-8 rounded-xl bg-background-100"
                data-readdy-form=""
              >
                <h3 className="text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>

                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-colors resize-none"
                      placeholder="请输入消息内容（最多500字）"
                    ></textarea>
                  </div>

                  <div className="anti-spam-field">
                    <label htmlFor="contact-phone-alt">备用电话</label>
                    <input
                      type="text"
                      id="contact-phone-alt"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formState === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                      消息已发送成功，我们会尽快回复！
                    </div>
                  )}
                  {formState === 'error' && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}